/** Coordinates must be complete, finite numbers; zero is a valid coordinate. */
export function hasCoordinates(race) {
  return Number.isFinite(race.lat) && Number.isFinite(race.lng)
    && Math.abs(race.lat) <= 90 && Math.abs(race.lng) <= 180;
}

/** Group co-located races, without merging different places that share a name. */
export function raceLocationData(races) {
  const groups = new Map();
  const countries = new Set();
  const unmapped = [];
  let mappedCount = 0;
  for (const race of races) {
    if (!hasCoordinates(race)) { unmapped.push(race); continue; }
    mappedCount++;
    const country = race.country?.trim();
    if (country) countries.add(country.toLocaleLowerCase());
    const key = `${race.lat.toFixed(3)},${race.lng.toFixed(3)}`;
    if (!groups.has(key)) groups.set(key, {
      key, lat: race.lat, lng: race.lng,
      name: race.location?.trim() || country || 'Unnamed location',
      country, races: [],
    });
    groups.get(key).races.push(race);
  }
  const places = [...groups.values()].sort((a, b) =>
    b.races.length - a.races.length || a.name.localeCompare(b.name));
  for (const place of places) {
    place.races.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  }
  return { places, countries: countries.size, mappedCount, unmapped };
}

/** Use the shortest longitude span, including journeys across the date line. */
export function mapPositions(places) {
  if (!places.length) return [];
  const longitudes = places.map(p => (p.lng + 360) % 360).sort((a, b) => a - b);
  let largestGap = -1;
  let start = longitudes[0];
  for (let i = 0; i < longitudes.length; i++) {
    const next = longitudes[(i + 1) % longitudes.length];
    const gap = (i === longitudes.length - 1 ? next + 360 : next) - longitudes[i];
    if (gap > largestGap) { largestGap = gap; start = next; }
  }
  const shifted = places.map(place => {
    let lng = (place.lng + 360) % 360;
    if (lng < start) lng += 360;
    return [place.lat, lng];
  });
  // Prefer the familiar world copy when the journey doesn't cross the date line.
  const offset = start > 180 ? 360 : 0;
  return shifted.map(([lat, lng]) => [lat, lng - offset]);
}

export function raceLocationDetail(race) {
  const parts = [];
  if (Number.isFinite(race.distance) && race.distance > 0) {
    parts.push(`${Number(race.distance.toFixed(2))} km`);
  }
  if (race.date) {
    const date = /^\d{4}-\d{2}-\d{2}$/.test(race.date) ? new Date(`${race.date}T12:00:00Z`) : null;
    parts.push(date && Number.isFinite(date.getTime())
      ? date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
      : race.date);
  }
  if (race.time) parts.push(race.time);
  if (race.type === 'trail') parts.push('Trail');
  if (race.isPR) parts.push('Personal best');
  return parts.join(' · ');
}

/** Merge only geocoding fields for unchanged races; keep all current edits. */
export function mergeRaceCoordinates(races, savedRaces) {
  const saved = new Map(savedRaces.filter(r => r.id).map(r => [r.id, r]));
  let updated = 0;
  for (const race of races) {
    const match = saved.get(race.id);
    if (!match || match.location !== race.location || !hasCoordinates(match)) continue;
    if (race.lat !== match.lat || race.lng !== match.lng || race.country !== match.country) {
      Object.assign(race, { lat: match.lat, lng: match.lng, country: match.country });
      updated++;
    }
  }
  return updated;
}
